import React from 'react';
import { useField } from 'formik';

import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface FormTextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    name: string;
    label?: string;
    helpText?: string;
    validate?: (value: unknown) => string | undefined;
}

export const FormTextareaField: React.FC<FormTextareaFieldProps> = ({
    label,
    helpText,
    className,
    validate,
    ...props
}) => {
    const validateFn = (value: unknown) => {
        if (props.required && (value === undefined || value === null || value === '')) {
            return 'Campo obrigatório';
        }

        if (validate) return validate(value);
        return undefined;
    };

    const [field, meta] = useField({ ...props, validate: validateFn });

    return (
        <div className={cn('space-y-2', className)}>
            {label && (
                <div className="space-y-1">
                    <Label
                        htmlFor={props.id || props.name}
                        className={cn(meta.touched && meta.error && 'text-destructive')}
                    >
                        {label} {props.required ? '*' : ''}
                    </Label>
                    {helpText ? (
                        <p className="text-sm leading-relaxed text-muted-foreground">{helpText}</p>
                    ) : null}
                </div>
            )}
            <Textarea
                {...field}
                {...props}
                id={props.id || props.name}
                className={cn(
                    'min-h-32 resize-y',
                    meta.touched &&
                        meta.error &&
                        'border-destructive focus-visible:ring-destructive',
                )}
            />
            {meta.touched && meta.error ? (
                <p className="text-[0.8rem] font-medium text-destructive">{meta.error}</p>
            ) : null}
        </div>
    );
};
